// ============================================================================
// CREATE PROJECT TASK MODAL
// Multi-step wizard for creating project-scoped async multi-agent tasks
// Adapted from CreateSessionTaskModal with multi-session context selection
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  X,
  ChevronRight,
  ChevronLeft,
  CheckCircle,
  Circle,
  Clock,
  BarChart3,
  GitBranch,
  FileText,
  Loader,
  ClipboardCheck,
  ChevronDown
} from 'lucide-react';
import {
  SessionTaskTemplate
} from '../../types/session-task.types';
import sessionTasksService, {
  projectTasksService,
  CreateProjectTaskInput
} from '../../services/sessionTasksService';
import { api } from '../../services/api';

interface CreateProjectTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  onSuccess: () => void;
}

type WizardStep = 'template' | 'customize' | 'context' | 'review';

const STEPS: { id: WizardStep; label: string }[] = [
  { id: 'template', label: '1. Template' },
  { id: 'customize', label: '2. Customize' },
  { id: 'context', label: '3. Context' },
  { id: 'review', label: '4. Review' },
];

const STEP_SUBTITLES: Record<WizardStep, string> = {
  template: 'Select a Task Template',
  customize: 'Customize Task Details',
  context: 'Select Context from Sessions',
  review: 'Review and Launch Task',
};

const getTemplateIcon = (templateId: string) => {
  if (templateId.includes('bcaf') || templateId.includes('assessment')) return <ClipboardCheck size={24} />;
  if (templateId.includes('diagram') || templateId.includes('architecture')) return <GitBranch size={24} />;
  if (templateId.includes('story') || templateId.includes('synthesis')) return <FileText size={24} />;
  return <BarChart3 size={24} />;
};

interface SessionData {
  sessionId: string;
  customerName?: string;
  name?: string;
  status?: string;
}

export const CreateProjectTaskModal: React.FC<CreateProjectTaskModalProps> = ({
  isOpen,
  onClose,
  projectId,
  onSuccess
}) => {
  const [currentStep, setCurrentStep] = useState<WizardStep>('template');
  const [templates, setTemplates] = useState<SessionTaskTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<SessionTaskTemplate | null>(null);
  const [taskName, setTaskName] = useState('');
  const [customContext, setCustomContext] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Multi-session context state
  const [sessions, setSessions] = useState<SessionData[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [expandedSessions, setExpandedSessions] = useState<Set<string>>(new Set());
  const [selectedSessionIds, setSelectedSessionIds] = useState<string[]>([]);

  // Context items per session (loaded on-demand)
  const [sessionContexts, setSessionContexts] = useState<Record<string, {
    transcripts: Array<{ id: string; timestamp: string; role: string; preview: string }>;
    requirements: Array<{ id: string; title: string; description: string }>;
    personas: Array<{ id: string; name: string; role: string }>;
    avatars: Array<{ id: string; name: string; role: string }>;
  }>>({});
  const [loadingSessionContext, setLoadingSessionContext] = useState<string | null>(null);

  // Selected context items across all sessions
  const [selectedTranscriptIds, setSelectedTranscriptIds] = useState<string[]>([]);
  const [selectedRequirementIds, setSelectedRequirementIds] = useState<string[]>([]);
  const [selectedPersonaIds, setSelectedPersonaIds] = useState<string[]>([]);
  const [selectedAvatarIds, setSelectedAvatarIds] = useState<string[]>([]);

  const currentStepIndex = STEPS.findIndex(s => s.id === currentStep);

  const loadTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    try {
      const loadedTemplates = await sessionTasksService.getTaskTemplates();
      setTemplates(loadedTemplates);
    } catch (err: any) {
      setError('Failed to load templates: ' + err.message);
    } finally {
      setLoadingTemplates(false);
    }
  }, []);

  const loadSessions = useCallback(async () => {
    setLoadingSessions(true);
    try {
      const response = await api.get(`/api/projects/${projectId}/sessions`);
      setSessions(response.data.sessions || []);
    } catch (err: any) {
      setError('Failed to load sessions: ' + err.message);
    } finally {
      setLoadingSessions(false);
    }
  }, [projectId]);

  // Load templates when modal opens
  useEffect(() => {
    if (isOpen && templates.length === 0) {
      loadTemplates();
    }
  }, [isOpen, templates.length, loadTemplates]);

  // Load sessions when reaching context step
  useEffect(() => {
    if (currentStep === 'context' && sessions.length === 0) {
      loadSessions();
    }
  }, [currentStep, sessions.length, loadSessions]);

  const loadSessionContext = async (sessionId: string) => {
    if (sessionContexts[sessionId]) return;
    setLoadingSessionContext(sessionId);
    try {
      const response = await api.get(`/api/sessions/${sessionId}/context`);
      const ctx = response.data;
      setSessionContexts(prev => ({
        ...prev,
        [sessionId]: {
          transcripts: ctx.transcriptEntries || [],
          requirements: ctx.requirements || [],
          personas: ctx.personas || [],
          avatars: ctx.sessionAvatars || []
        }
      }));
    } catch (err: any) {
      // If context endpoint doesn't exist, provide empty context
      setSessionContexts(prev => ({
        ...prev,
        [sessionId]: { transcripts: [], requirements: [], personas: [], avatars: [] }
      }));
    } finally {
      setLoadingSessionContext(null);
    }
  };

  const toggleSession = (sessionId: string) => {
    setExpandedSessions(prev => {
      const next = new Set(prev);
      if (next.has(sessionId)) {
        next.delete(sessionId);
      } else {
        next.add(sessionId);
        loadSessionContext(sessionId);
      }
      return next;
    });
  };

  const toggleSessionSelection = (sessionId: string) => {
    if (selectedSessionIds.includes(sessionId)) {
      setSelectedSessionIds(prev => prev.filter(id => id !== sessionId));
    } else {
      setSelectedSessionIds(prev => [...prev, sessionId]);
      // Expand to show context
      if (!expandedSessions.has(sessionId)) {
        toggleSession(sessionId);
      }
    }
  };

  const toggleItem = (id: string, list: string[], setList: (v: string[]) => void) => {
    if (list.includes(id)) {
      setList(list.filter(i => i !== id));
    } else {
      setList([...list, id]);
    }
  };

  const handleTemplateSelect = (template: SessionTaskTemplate) => {
    setSelectedTemplate(template);
    setTaskName(template.name);
    setCurrentStep('customize');
  };

  const handleNext = () => {
    if (currentStep === 'template' && selectedTemplate) setCurrentStep('customize');
    else if (currentStep === 'customize') setCurrentStep('context');
    else if (currentStep === 'context') setCurrentStep('review');
  };

  const handleBack = () => {
    if (currentStep === 'customize') setCurrentStep('template');
    else if (currentStep === 'context') setCurrentStep('customize');
    else if (currentStep === 'review') setCurrentStep('context');
  };

  const handleSubmit = async () => {
    if (!selectedTemplate) return;

    setIsSubmitting(true);
    setError(null);

    try {
      let taskCustomContext: Record<string, any> | undefined;
      if (customContext.trim()) {
        taskCustomContext = { text: customContext.trim() };
      }

      const input: CreateProjectTaskInput = {
        taskTemplateId: selectedTemplate.templateId,
        taskName: taskName.trim(),
        taskCategory: selectedTemplate.capabilities.sessionTaskCategory,
        contextSnapshot: {
          transcriptEntryIds: selectedTranscriptIds,
          requirementIds: selectedRequirementIds,
          personaIds: selectedPersonaIds,
          avatarIds: selectedAvatarIds,
          customContext: taskCustomContext
        },
        sourceSessionIds: selectedSessionIds
      };

      await projectTasksService.createTask(projectId, input);
      onSuccess();
      handleClose();
    } catch (err: any) {
      setError('Failed to create task: ' + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setCurrentStep('template');
      setSelectedTemplate(null);
      setTaskName('');
      setCustomContext('');
      setSelectedSessionIds([]);
      setSelectedTranscriptIds([]);
      setSelectedRequirementIds([]);
      setSelectedPersonaIds([]);
      setSelectedAvatarIds([]);
      setError(null);
      onClose();
    }
  };

  const isNextDisabled =
    !selectedTemplate ||
    (currentStep === 'customize' && !taskName.trim()) ||
    isSubmitting;

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={handleClose}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[1000] flex items-center justify-center"
      >
        {/* Modal */}
        <div
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-xl shadow-2xl max-w-[900px] w-[90%] max-h-[90vh] flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="px-8 pt-8 pb-4 flex justify-between items-start">
            <div className="flex flex-col gap-1">
              <h1 className="text-slate-900 text-2xl font-bold leading-tight m-0">
                Create Project Task
              </h1>
              <p className="text-slate-500 text-sm font-normal m-0">
                Step {currentStepIndex + 1}: {STEP_SUBTITLES[currentStep]}
              </p>
            </div>
            <button
              onClick={handleClose}
              disabled={isSubmitting}
              className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-500 disabled:cursor-not-allowed"
            >
              <X size={20} />
            </button>
          </div>

          {/* Step Indicator */}
          <div className="px-8 py-4 flex gap-3 border-b border-slate-100">
            {STEPS.map((step, index) => {
              const isActive = step.id === currentStep;
              const isCompleted = index < currentStepIndex;
              let stepClasses: string;
              if (isActive) stepClasses = 'bg-primary text-white font-semibold shadow-sm';
              else if (isCompleted) stepClasses = 'bg-emerald-500 text-white font-semibold';
              else stepClasses = 'bg-slate-100 text-slate-500 font-medium';
              return (
                <div
                  key={step.id}
                  className={`flex h-9 items-center justify-center gap-x-2 rounded-full px-4 text-sm transition-colors ${stepClasses}`}
                >
                  {isCompleted ? <CheckCircle size={16} /> : <Circle size={16} />}
                  <span>{step.label}</span>
                </div>
              );
            })}
          </div>

          {/* Content */}
          <div className="p-8 overflow-y-auto flex-1">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 p-3 rounded-lg mb-4 text-sm">
                {error}
              </div>
            )}

            {/* Step 1: Template Selection */}
            {currentStep === 'template' && (
              <div>
                {loadingTemplates ? (
                  <div className="text-center py-10 text-slate-500">
                    <Loader size={24} className="animate-spin mx-auto mb-3" />
                    Loading templates...
                  </div>
                ) : (
                  <table className="w-full text-left">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="pb-3 text-xs font-semibold text-slate-500 uppercase tracking-wider w-8"></th>
                        <th className="pb-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Template</th>
                        <th className="pb-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Category</th>
                        <th className="pb-3 text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Duration</th>
                      </tr>
                    </thead>
                    <tbody>
                      {templates.map(template => {
                        const isSelected = selectedTemplate?.templateId === template.templateId;
                        return (
                          <tr
                            key={template.templateId}
                            onClick={() => handleTemplateSelect(template)}
                            className={`cursor-pointer border-b border-slate-100 transition-colors ${
                              isSelected ? 'bg-primary/5' : 'hover:bg-slate-50'
                            }`}
                          >
                            <td className="py-3 pr-2">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                                isSelected ? 'bg-primary/20 text-primary' : 'bg-slate-100 text-slate-500'
                              }`}>
                                {getTemplateIcon(template.templateId)}
                              </div>
                            </td>
                            <td className="py-3 pr-4">
                              <div className="flex items-center gap-2">
                                <span className={`text-sm font-semibold ${isSelected ? 'text-primary' : 'text-slate-900'}`}>
                                  {template.name}
                                </span>
                                {isSelected && <CheckCircle size={14} className="text-primary shrink-0" />}
                              </div>
                              <p className="text-xs text-slate-500 mt-0.5 m-0 line-clamp-1">
                                {template.description}
                              </p>
                            </td>
                            <td className="py-3 pr-4">
                              <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded ${
                                template.capabilities.sessionTaskCategory === 'thinking'
                                  ? 'bg-blue-100 text-blue-700'
                                  : 'bg-amber-100 text-amber-700'
                              }`}>
                                {template.capabilities.sessionTaskCategory}
                              </span>
                            </td>
                            <td className="py-3 text-right">
                              <span className="inline-flex items-center gap-1.5 text-xs text-slate-500 font-medium">
                                <Clock size={12} />
                                {template.capabilities.estimatedDuration}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* Step 2: Customize */}
            {currentStep === 'customize' && selectedTemplate && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Task Name</label>
                  <input
                    type="text"
                    value={taskName}
                    onChange={(e) => setTaskName(e.target.value)}
                    placeholder="Enter a descriptive name for this task"
                    className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Template Details</label>
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-2">
                    <p className="text-sm text-slate-900 m-0"><strong>Category:</strong> {selectedTemplate.capabilities.sessionTaskCategory}</p>
                    <p className="text-sm text-slate-900 m-0"><strong>Duration:</strong> {selectedTemplate.capabilities.estimatedDuration}</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Usage Notes</label>
                  <ul className="m-0 pl-5 text-sm text-slate-500 leading-relaxed space-y-1">
                    {selectedTemplate.metadata.usageNotes.map((note) => (
                      <li key={note}>{note}</li>
                    ))}
                  </ul>
                </div>

              </div>
            )}

            {/* Step 3: Context Selection (multi-session) */}
            {currentStep === 'context' && (
              <div className="space-y-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                  Select sessions and their context items to provide input for this task. Expand a session to pick specific items.
                </div>

                {loadingSessions && (
                  <div className="text-center py-10 text-slate-500">
                    <Loader size={24} className="animate-spin mx-auto mb-3" />
                    Loading sessions...
                  </div>
                )}
                {!loadingSessions && sessions.length === 0 && (
                  <div className="text-center py-10 text-slate-400">
                    No sessions linked to this project yet.
                  </div>
                )}
                {!loadingSessions && sessions.length > 0 && (
                  <div className="space-y-3">
                    {sessions.map(session => {
                      const sessionId = session.sessionId;
                      const isSelected = selectedSessionIds.includes(sessionId);
                      const isExpanded = expandedSessions.has(sessionId);
                      const ctx = sessionContexts[sessionId];
                      const isLoadingCtx = loadingSessionContext === sessionId;
                      let statusDotClass: string;
                      if (session.status === 'active') statusDotClass = 'bg-green-500';
                      else if (session.status === 'ended') statusDotClass = 'bg-slate-400';
                      else statusDotClass = 'bg-amber-400';

                      return (
                        <div key={sessionId} className="border border-slate-200 rounded-xl overflow-hidden bg-white">
                          {/* Session Header */}
                          <div className="flex items-center gap-3 p-4 cursor-pointer hover:bg-slate-50 transition-colors">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleSessionSelection(sessionId)}
                              className="rounded border-slate-300 text-primary focus:ring-primary/30"
                            />
                            <div
                              className="flex-1 flex items-center gap-2"
                              onClick={() => toggleSession(sessionId)}
                            >
                              {isExpanded ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
                              <span className={`w-2 h-2 rounded-full shrink-0 ${statusDotClass}`} />
                              <span className="text-sm font-medium text-slate-900">
                                {session.customerName || session.name || sessionId}
                              </span>
                            </div>
                          </div>

                          {/* Expanded Context */}
                          {isExpanded && (
                            <div className="border-t border-slate-100 p-4 bg-slate-50/50">
                              {isLoadingCtx && (
                                <div className="text-center py-4 text-slate-400 text-sm">
                                  <Loader size={16} className="animate-spin inline mr-2" />
                                  Loading context...
                                </div>
                              )}
                              {!isLoadingCtx && ctx && (
                                <div className="space-y-4">
                                  {/* Requirements */}
                                  {ctx.requirements.length > 0 && (
                                    <div>
                                      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                                        Requirements ({ctx.requirements.filter(r => selectedRequirementIds.includes(r.id)).length}/{ctx.requirements.length})
                                      </label>
                                      <div className="max-h-[150px] overflow-y-auto border border-slate-200 rounded-lg bg-white">
                                        {ctx.requirements.map(req => (
                                          <div
                                            key={req.id}
                                            onClick={() => toggleItem(req.id, selectedRequirementIds, setSelectedRequirementIds)}
                                            className={`p-2.5 border-b border-slate-50 cursor-pointer flex gap-2 items-start transition-colors ${
                                              selectedRequirementIds.includes(req.id) ? 'bg-primary/5' : 'hover:bg-slate-50'
                                            }`}
                                          >
                                            <input
                                              type="checkbox"
                                              checked={selectedRequirementIds.includes(req.id)}
                                              onChange={() => {}}
                                              className="mt-0.5 rounded border-slate-300 text-primary focus:ring-primary/30"
                                            />
                                            <div className="flex-1 min-w-0">
                                              <div className="text-sm font-medium text-slate-900">{req.title}</div>
                                              <div className="text-xs text-slate-500 truncate">{req.description.substring(0, 80)}</div>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Personas */}
                                  {ctx.personas.length > 0 && (
                                    <div>
                                      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                                        Personas ({ctx.personas.filter(p => selectedPersonaIds.includes(p.id)).length}/{ctx.personas.length})
                                      </label>
                                      <div className="flex flex-wrap gap-2">
                                        {ctx.personas.map(persona => (
                                          <div
                                            key={persona.id}
                                            onClick={() => toggleItem(persona.id, selectedPersonaIds, setSelectedPersonaIds)}
                                            className={`px-3 py-1.5 rounded-lg cursor-pointer text-sm transition-all ${
                                              selectedPersonaIds.includes(persona.id)
                                                ? 'border-2 border-primary bg-primary/5 text-primary font-medium'
                                                : 'border border-slate-200 hover:border-primary/50 hover:bg-primary/5'
                                            }`}
                                          >
                                            {persona.name} ({persona.role})
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Avatars */}
                                  {ctx.avatars.length > 0 && (
                                    <div>
                                      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                                        Avatars ({ctx.avatars.filter(a => selectedAvatarIds.includes(a.id)).length}/{ctx.avatars.length})
                                      </label>
                                      <div className="flex flex-wrap gap-2">
                                        {ctx.avatars.map(avatar => (
                                          <div
                                            key={avatar.id}
                                            onClick={() => toggleItem(avatar.id, selectedAvatarIds, setSelectedAvatarIds)}
                                            className={`px-3 py-1.5 rounded-lg cursor-pointer text-sm transition-all ${
                                              selectedAvatarIds.includes(avatar.id)
                                                ? 'border-2 border-primary bg-primary/5 text-primary font-medium'
                                                : 'border border-slate-200 hover:border-primary/50 hover:bg-primary/5'
                                            }`}
                                          >
                                            {avatar.name} ({avatar.role})
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Transcripts */}
                                  {ctx.transcripts.length > 0 && (
                                    <div>
                                      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                                        Transcript Entries ({ctx.transcripts.filter(t => selectedTranscriptIds.includes(t.id)).length}/{ctx.transcripts.length})
                                      </label>
                                      <div className="max-h-[150px] overflow-y-auto border border-slate-200 rounded-lg bg-white">
                                        {ctx.transcripts.map(entry => (
                                          <div
                                            key={entry.id}
                                            onClick={() => toggleItem(entry.id, selectedTranscriptIds, setSelectedTranscriptIds)}
                                            className={`p-2.5 border-b border-slate-50 cursor-pointer flex gap-2 items-start transition-colors ${
                                              selectedTranscriptIds.includes(entry.id) ? 'bg-primary/5' : 'hover:bg-slate-50'
                                            }`}
                                          >
                                            <input
                                              type="checkbox"
                                              checked={selectedTranscriptIds.includes(entry.id)}
                                              onChange={() => {}}
                                              className="mt-0.5 rounded border-slate-300 text-primary focus:ring-primary/30"
                                            />
                                            <div className="flex-1 min-w-0">
                                              <div className="text-xs text-slate-400 mb-0.5">
                                                {entry.role} &bull; {new Date(entry.timestamp).toLocaleString()}
                                              </div>
                                              <div className="text-sm text-slate-700 truncate">{entry.preview}</div>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {ctx.transcripts.length === 0 && ctx.requirements.length === 0 && ctx.personas.length === 0 && ctx.avatars.length === 0 && (
                                    <p className="text-sm text-slate-400 text-center py-2">No context items found for this session.</p>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Custom Context */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Custom Context (Optional)
                  </label>
                  <textarea
                    value={customContext}
                    onChange={(e) => setCustomContext(e.target.value)}
                    placeholder="Add any additional context, instructions, or focus areas for the task..."
                    rows={3}
                    className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm font-[inherit] resize-y focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                  />
                </div>
              </div>
            )}

            {/* Step 4: Review */}
            {currentStep === 'review' && selectedTemplate && (
              <div className="space-y-5">
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
                  <h3 className="m-0 mb-4 text-base font-semibold text-slate-900">Task Summary</h3>
                  <div className="space-y-3">
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm font-medium text-slate-500 w-36 shrink-0">Template:</span>
                      <span className="text-sm text-slate-900">{selectedTemplate.name}</span>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm font-medium text-slate-500 w-36 shrink-0">Task Name:</span>
                      <span className="text-sm text-slate-900">{taskName}</span>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm font-medium text-slate-500 w-36 shrink-0">Category:</span>
                      <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded ${
                        selectedTemplate.capabilities.sessionTaskCategory === 'thinking'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-amber-100 text-amber-700'
                      }`}>
                        {selectedTemplate.capabilities.sessionTaskCategory}
                      </span>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm font-medium text-slate-500 w-36 shrink-0">Est. Duration:</span>
                      <span className="text-sm text-slate-900">{selectedTemplate.capabilities.estimatedDuration}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
                  <h3 className="m-0 mb-4 text-base font-semibold text-slate-900">Selected Context</h3>
                  <div className="space-y-3">
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm font-medium text-slate-500 w-36 shrink-0">Sessions:</span>
                      <span className="text-sm text-slate-900">{selectedSessionIds.length}</span>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm font-medium text-slate-500 w-36 shrink-0">Transcript Entries:</span>
                      <span className="text-sm text-slate-900">{selectedTranscriptIds.length}</span>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm font-medium text-slate-500 w-36 shrink-0">Requirements:</span>
                      <span className="text-sm text-slate-900">{selectedRequirementIds.length}</span>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm font-medium text-slate-500 w-36 shrink-0">Personas:</span>
                      <span className="text-sm text-slate-900">{selectedPersonaIds.length}</span>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm font-medium text-slate-500 w-36 shrink-0">Avatars:</span>
                      <span className="text-sm text-slate-900">{selectedAvatarIds.length}</span>
                    </div>
                    {customContext && (
                      <div>
                        <span className="text-sm font-medium text-slate-500 block mb-1">Custom Context:</span>
                        <div className="text-sm text-slate-900 p-2 bg-white rounded-lg whitespace-pre-wrap">
                          {customContext}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-8 py-6 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between">
            <button
              onClick={handleBack}
              disabled={currentStep === 'template' || isSubmitting}
              className={`flex items-center justify-center gap-2 px-5 h-11 rounded-lg border text-sm font-semibold transition-all ${
                currentStep === 'template' || isSubmitting
                  ? 'border-slate-200 text-slate-400 cursor-not-allowed'
                  : 'border-slate-300 text-slate-700 hover:bg-slate-100'
              }`}
            >
              <ChevronLeft size={16} />
              Back
            </button>

            <div className="flex gap-3">
              <button
                onClick={handleClose}
                disabled={isSubmitting}
                className="flex items-center justify-center px-6 h-11 rounded-lg border border-slate-300 text-slate-700 font-semibold text-sm hover:bg-slate-100 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>

              {currentStep !== 'review' ? (
                <button
                  onClick={handleNext}
                  disabled={isNextDisabled}
                  className="flex items-center justify-center gap-2 px-8 h-11 rounded-lg bg-primary text-white font-bold text-sm shadow-lg shadow-primary/20 transition-all hover:brightness-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
                >
                  Next
                  <ChevronRight size={16} />
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="flex items-center justify-center gap-2 px-8 h-11 rounded-lg bg-emerald-500 text-white font-bold text-sm shadow-lg shadow-emerald-500/20 transition-all hover:brightness-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
                >
                  {isSubmitting ? (
                    <>
                      <Loader size={16} className="animate-spin" />
                      Launching...
                    </>
                  ) : (
                    'Launch Task'
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
